import { useEffect } from 'react';
import {
    DataLayerClassName,
    DataLayerEvent,
    DataLayerEventCategory,
    DataLayerEventLabel,
    useDataLayer,
} from '../../../../client';
import { RecommendationCarouselType } from '../../../../server';
import { DataLayerUtils } from '../DataLayerUtils';

interface UseCarouselTrackingProps {
    trackEvent: ReturnType<typeof useDataLayer>['trackEvent'];
    t2SProductEventClick: (trackingId: string) => () => void;
}

export const useCarouselTracking = ({ trackEvent, t2SProductEventClick }: UseCarouselTrackingProps) => {
    useEffect(() => {
        const carousels = document.querySelectorAll(`.${DataLayerClassName.targetedCarousel}`);

        if (!carousels.length) return;

        const handler = (event: Event) => {
            const target = event.target as HTMLElement;

            // T2S TRACKING
            const productLink = target.closest(`.${DataLayerClassName.productTile}`);

            const dataTrackingIdT2S = productLink?.getAttribute('data-layer-id');
            if (dataTrackingIdT2S) {
                t2SProductEventClick(dataTrackingIdT2S)();
            }

            // AB TEST - POTION ANALYTICS
            const productTileElement = target.closest('[data-product-id]');
            if (!productTileElement) return;

            const dataProductId = productTileElement?.getAttribute('data-product-id') ?? DataLayerEventLabel.Null;

            const carouselElement = productTileElement.closest('[data-layer-id]');

            const carouselId = carouselElement?.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
            const spaceId = DataLayerUtils.extractSpaceId(carouselId);

            const carouselType = carouselElement?.getAttribute('data-layer-type') ?? RecommendationCarouselType.T2S;

            trackEvent(DataLayerEvent.Click, {
                event: 'navigation',
                category: DataLayerEventCategory.Carousel,
                action: 'click',
                label: dataProductId,
                space_id: spaceId,
                asset_type: carouselType.toLocaleLowerCase(),
            });
        };

        const carouselArray = Array.from(carousels);

        carouselArray.forEach((carousel) => {
            carousel.addEventListener('click', handler);
        });

        return () => {
            carouselArray.forEach((carousel) => {
                carousel.removeEventListener('click', handler);
            });
        };
    }, [t2SProductEventClick, trackEvent]);

    // Carousel impression tracking
    useEffect(() => {
        const observerRefs: IntersectionObserver[] = [];
        const selector = `.${DataLayerClassName.targetedCarousel}`;

        const trackImpression = (element: Element) => {
            const carouselElement = element.querySelector('[data-layer-id]');
            const carouselId = carouselElement?.getAttribute('data-layer-id');
            if (!carouselId) return;

            const spaceId = DataLayerUtils.extractSpaceId(carouselId);
            const carouselType = carouselElement?.getAttribute('data-layer-type') ?? RecommendationCarouselType.T2S;

            trackEvent(DataLayerEvent.Impression, {
                event: 'navigation',
                category: DataLayerEventCategory.Carousel,
                action: 'impression',
                label: '',
                space_id: spaceId,
                asset_type: carouselType.toLocaleLowerCase(),
            });
        };

        const setupImpressionTracking = (): boolean => {
            const elements = document.querySelectorAll(selector);
            if (!elements.length) return false;

            DataLayerUtils.addImpressionObserver(selector, trackImpression, observerRefs, { threshold: 0.5 });
            return true;
        };

        if (!setupImpressionTracking()) {
            const mutationObserver = new MutationObserver(() => {
                if (setupImpressionTracking()) {
                    mutationObserver.disconnect();
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });

            return () => mutationObserver.disconnect();
        }

        return () => {
            observerRefs.forEach((obs) => obs.disconnect());
        };
    }, [trackEvent]);
};
